import { dasa } from './dasaClasses'
import Swal from 'sweetalert2'
export namespace test {
    export class DasaAppUtil {

        /**
         * QR Table Ordering PAYMENT-stage age gate. Call when checkout is
         * pressed (from any checkout button) so the prompt shows over the
         * current page. Resolves true when the customer may proceed — gate
         * off, not a table order, already verified, or they confirmed 18+ —
         * and false when they declined (the caller should stay on the page).
         */
        public static confirmAgeForCheckout(): Promise<boolean> {
            const u: any = dasa.Utility as any;
            if (u.orderMode !== 'TABLE') return Promise.resolve(true);
            if (u.ageVerified) return Promise.resolve(true);
            const cfg: any = dasa.Utility.storeConfigurations || {};
            if (!cfg.ageVerificationEnabled) return Promise.resolve(true);
            if (String(cfg.ageVerificationStage || '').toUpperCase() !== 'PAYMENT') return Promise.resolve(true);
            return Swal.fire({
                title: 'Age verification',
                text: 'Are you 18 years of age or older?',
                icon: 'question',
                showCancelButton: true,
                confirmButtonText: 'Yes, I am 18+',
                cancelButtonText: 'No',
                allowOutsideClick: false
            }).then((r) => {
                if (r.isConfirmed) {
                    u.ageVerified = true;
                    return true;
                }
                // Declined — stay on the current page.
                Swal.fire({ text: 'Sorry, you must be 18 or over to order.', icon: 'warning', timer: 3000 });
                return false;
            });
        }
        /**
         * Method return gender string by code
         * @param genderCode 
         * @returns 
         */
        public static getGenderString(genderCode: string): string {
            let str: string = ""
            switch (genderCode) {
                case dasa.Constant.GENDER_MALE:
                    return 'Male';
                case dasa.Constant.GENDER_FEMALE:
                    return 'Female';
                case dasa.Constant.GENDER_TRANSGENDER:
                    return 'Transgender';
                default:
                    return "";
            }
        }

        /**
         * Method used to get Feedback type string
         * @param type
         * @returns 
         */

        public static getFeedbackType(type: number): string {
            let str: string = ""
            switch (type) {
                case dasa.Constant.FEEDBACK_TYPE_FEEDBACK:
                    return 'Feedback';
                case dasa.Constant.FEEDBACK_TYPE_SUGGESTION:
                    return 'Suggestion';
                case dasa.Constant.FEEDBACK_TYPE_COMPLAIN:
                    return 'Complain';
                default:
                    return "";
            }
        }

        /**
         * Method used to get Feedback status string
         * @param status 
         * @returns 
         */

        public static getFeedbackStatus(status: number): string {
            let str: string = ""
            switch (status) {
                case dasa.Constant.FEEDBACK_STATUS_CLOSED:
                    return 'Closed';
                case dasa.Constant.FEEDBACK_STATUS_OPEN:
                    return 'Open';
                default:
                    return "";
            }
        }

        public static getOrderStatus(status: number): string {
            let str: string = ""
            switch (status) {
                case dasa.Constant.ORDER_TYPE_ACCEPTED:
                    return 'Accepted';
                case dasa.Constant.ORDER_TYPE_CANCELED:
                    return 'Canceled';
                case dasa.Constant.ORDER_TYPE_DELIVERED:
                    return 'Delivered';
                case dasa.Constant.ORDER_TYPE_PENDING:
                    return 'Pending';
                case dasa.Constant.ORDER_TYPE_PICKED:
                    return 'Picked';
                case dasa.Constant.ORDER_TYPE_PROCESSING:
                    // Shop stores (explicit ecommerceEnabled) aren't cooking anything.
                    return dasa.Utility.storeConfigurations?.ecommerceEnabled === true
                        ? 'Accepted, Processing Your Order'
                        : 'Accepted, Preparing Your Food';
                case dasa.Constant.ORDER_TYPE_REJECTED:
                    return 'Rejected';
                case dasa.Constant.ORDER_TYPE_READY_FOR_PICKUP:
                    return dasa.Utility.storeConfigurations?.ecommerceEnabled === true ? 'Ready for collection' : 'Ready For Pickup';
                case dasa.Constant.ORDER_TYPE_OUT_FOR_DELIVERY:
                    return 'Out for Delivery';
                case dasa.Constant.ORDER_TYPE_SEARCHING_FOR_COURIER:
                    return 'Searching for Courier';
                case dasa.Constant.ORDER_TYPE_COURIER_ASSIGNED:
                    return 'Courier Assigned';
                case dasa.Constant.ORDER_TYPE_COURIER_ARRIVING_PICKUP:
                    return dasa.Utility.storeConfigurations?.ecommerceEnabled === true ? 'Courier Collecting Your Order' : 'Courier Picking Up Your Food';
                case dasa.Constant.ORDER_TYPE_CUSTOMER_DROPOFF_IMMINENT:
                    return dasa.Utility.storeConfigurations?.ecommerceEnabled === true ? 'Courier Arriving with Your Order' : 'Courier Arriving with Your Food';
                case dasa.Constant.ORDER_TYPE_SCHEDULE_ACCEPTED:
                    return 'Accepted (Scheduled Order)';
                case dasa.Constant.ORDER_TYPE_CANCELLED_BY_VENDOR:
                    return 'Canceled by Vendor';
                case dasa.Constant.ORDER_TYPE_CANCELLED_BY_CUSTOMER:
                    return 'Canceled by Customer';
                default:
                    return "";
            }
        }

        /**
         * Make and return copy of an object
         * @param object
         * @returns 
         */

        public static getCopyOfAnObject(object: any): any {
            return JSON.parse(JSON.stringify(object));
        }

        /**
         * Identity of a cart line for merge purposes.
         *
         * Two lines are "the same" only when the customer would regard them as
         * interchangeable: same item, same chosen options and extras, same
         * size, same note. Anything that changes what the kitchen makes has to
         * change the signature, otherwise a burger with bacon would silently
         * merge into one without.
         */
        /**
         * Per-unit option price of a cart line, from the options actually chosen
         * on it (price × option quantity), the same way the printed receipt and
         * the vendor portal total a line.
         *
         * Basket and checkout used the line's stored optionPrice instead. That
         * value is copied from running totals on the item popup, which carry
         * over from the previously added item until they are reset, so an item
         * could be saved with the previous item's option price — order 187989
         * was charged £63.00 for £50.10 of food. B1G1 and reward lines price
         * their options by their own rules and keep the stored value.
         */
        public static cartLineOptionUnitPrice(cart: any): number {
            if (!cart || cart.isB1G1 || cart.isReward) {
                return Number(cart?.optionPrice) || 0;
            }
            let total = 0;
            const sets = cart.storeItem?.optionSets || [];
            for (const set of sets) {
                for (const o of (set?.options || [])) {
                    const qty = Number(o?.quantity) || 0;
                    // optionchecked is absent on carts read back from the server;
                    // there an unselected option is stored with quantity 0.
                    const chosen = o?.optionchecked === true || (o?.optionchecked == null && qty > 0);
                    if (chosen && qty > 0) total += (Number(o.price) || 0) * qty;
                }
            }
            return Math.round((total + Number.EPSILON) * 100) / 100;
        }

        public static cartLineSignature(cart: any): string {
            const si: any = (cart && cart.storeItem) ? cart.storeItem : {};

            const extras = (si.extras || [])
                .filter((e: any) => e && (e.extrachecked || e.checked || (e.quantity || 0) > 0))
                .map((e: any) => (e.id) + ':' + (e.quantity == null ? 1 : e.quantity))
                .sort();

            const options: string[] = [];
            const sets = (si.optionSets && si.optionSets.length) ? si.optionSets : [];
            for (const os of sets) {
                for (const o of ((os && os.options) ? os.options : [])) {
                    if (o && o.optionchecked) {
                        options.push((os.id) + '.' + (o.id) + ':' + (o.quantity == null ? 1 : o.quantity));
                    }
                }
            }
            options.sort();

            return JSON.stringify({
                item: si.id,
                size: cart && cart.sizeCode ? cart.sizeCode : '',
                notes: (cart && cart.orderNotes ? String(cart.orderNotes) : '').trim().toLowerCase(),
                // Two lines with different artwork are two different orders, never one.
                artwork: cart && cart.artworkToken ? cart.artworkToken : '',
                extras: extras,
                options: options
            });
        }

        /**
         * Adds a line to the cart, folding it into an identical existing line
         * instead of appending a duplicate.
         *
         * Ordering the same thing twice used to create two separate cart lines,
         * which reached the kitchen display as "1x Chicken Buttermilk Burger"
         * printed twice rather than "2x" — easy for a kitchen to read as one
         * item and under-produce.
         *
         * Promotional and reward lines are never merged: B1G1 pairs a free line
         * with the paid one that earned it, so collapsing them would break that
         * pairing. Those keep the previous append-only behaviour.
         */
        public static addOrMergeCartLine(cart: any): void {
            if (dasa.Utility.carts == null || dasa.Utility.carts === undefined) {
                dasa.Utility.carts = [] as dasa.Cart[];
            }
            const carts: any[] = dasa.Utility.carts as any[];

            if (cart && (cart.isB1G1 || cart.isReward)) {
                carts.push(cart);
                return;
            }

            const signature = test.DasaAppUtil.cartLineSignature(cart);
            const existing = carts.find((c: any) =>
                c && !c.isB1G1 && !c.isReward && !c.voided && !c.sentToKitchen &&
                test.DasaAppUtil.cartLineSignature(c) === signature);

            if (!existing) {
                carts.push(cart);
                return;
            }

            // The incoming line already carries totals for its own quantity, so
            // summing gives correct totals for the combined line without having
            // to re-derive per-unit prices.
            //
            // optionPrice is the exception: it is PER UNIT (basket and checkout
            // multiply it by quantity), and a merged line has identical options,
            // so it stays as it is. Summing it charged the options twice over —
            // two "Kids meal + pizza" adds became qty 2 × £13 instead of × £6.50.
            const num = (v: any) => (typeof v === 'number' && isFinite(v)) ? v : 0;
            existing.quantity   = num(existing.quantity)   + num(cart.quantity);
            existing.price      = num(existing.price)      + num(cart.price);
            existing.extraPrice = num(existing.extraPrice) + num(cart.extraPrice);
            existing.tax        = num(existing.tax)        + num(cart.tax);
            existing.discount   = num(existing.discount)   + num(cart.discount);
            existing.netPayable = num(existing.netPayable) + num(cart.netPayable);
            if (existing.storeItem) {
                existing.storeItem.quantity = existing.quantity;
            }
        }

        /**
         * Method return User group string by userGroupId
         * @param userGroupId 
         * @returns 
         */

        public static getUserGroupString(userGroupId: number): string {
            switch (userGroupId) {
                case dasa.Constant.USER_TYPE_SUPER_ADMIN:
                    return 'Super Admin';
                case dasa.Constant.USER_TYPE_BUSINESS_ADMIN:
                    return 'Business Admin';
                case dasa.Constant.USER_TYPE_STORE_ADMIN:
                    return 'Store Admin';
                case dasa.Constant.USER_TYPE_DRIVER:
                    return 'Driver';
                case dasa.Constant.USER_TYPE_BUSINESS_ADMIN_cum_STORE_ADMIN:
                    return 'Business cum Super Admin';
                case dasa.Constant.USER_TYPE_KITCHEN_USER:
                    return 'Kitchen(KOT)';
                default:
                    return '';
            }
        }

        /**
         * Method used to get Status String
         */

        public static getStatusString(status: number): string {
            switch (status) {
                case dasa.Constant.STATUS_ENABLED:
                    return 'Enabled';
                case dasa.Constant.STATUS_DISABLED:
                    return 'Disabled';
                default:
                    return '';
            }
        }

        /**
         * 
         * @param deliveryType Method used to get deliveryType String
         */

        public static getDeliveryTypeString(deliveryType: number) {
            switch (deliveryType) {
                case dasa.Constant.DELIVERY_TYPE_PICK_UP_ONLY:
                    return 'Pickup only';
                case dasa.Constant.DELIVERY_TYPE_DELIVERY_ONLY:
                    return 'Delivery only';
                case dasa.Constant.DELIVERY_TYPE_PICKUP_DELIVERY_BOTH:
                    return 'Both';
                default:
                    return '';
            }
        }

        /**
         * Method used to get featured string
         */

        public static getFeaturedString(featuredType: number) {
            switch (featuredType) {
                case dasa.Constant.FEATURED_STORE_NO:
                    return 'No';
                case dasa.Constant.FEATURED_STORE_YES:
                    return 'Yes';
                default:
                    return '';
            }
        }

        /**
         * Method used to get store by store id
         * @param userId 
         * @returns 
         */

        public static getStoreById(storeId: number): dasa.Store {
            let store: dasa.Store[] = dasa.Utility.stores;
            let b: any = {};
            if (store != undefined && store.length > 0) {
                for (let i: any = 0; i < store.length; i++) {
                    if (storeId == store[i].id) {
                        b = store[i];
                        break;
                    }
                }
            }
            return b;
        }

        /* get only enable brands list*/
        public static getEnableBrandsList() {
            let brands: dasa.Brand[] = dasa.Utility.brands;
            let listDisabledStoreId = dasa.Utility.storeSettings?.disabledBrandIds;
            let b: any = [];
            if (listDisabledStoreId != undefined && listDisabledStoreId.length > 0) {
                if (brands != undefined && brands.length > 0) {
                    for (let i: any = 0; i < brands.length; i++) {
                        let isDisabled: boolean = false;
                        for (let k: any = 0; k < listDisabledStoreId.length; k++) {
                            if (listDisabledStoreId[k] == brands[i].id) {
                                isDisabled = true;
                                break;
                            }
                        }
                        if (isDisabled == false) {
                            b[b.length] = brands[i];
                        }
                    }
                }
            } else {
                b = brands;
            }
            return this.getCopyOfAnObject(b);
        }

        /* get only enable category list*/
        public static getEnableCategoryList() {
            let categories: dasa.Category[] = dasa.Utility.categories;
            let disabledCategoryIds = dasa.Utility.storeSettings?.disabledCategoryIds;
            let b: any = [];
            if (disabledCategoryIds != undefined && disabledCategoryIds.length > 0) {
                if (categories != undefined && categories.length > 0) {
                    for (let i: any = 0; i < categories.length; i++) {
                        let isDisabled: boolean = false;
                        for (let k: any = 0; k < disabledCategoryIds.length; k++) {
                            if (disabledCategoryIds[k] == categories[i].id) {
                                isDisabled = true;
                                break;
                            }
                        }
                        if (isDisabled == false) {
                            b[b.length] = categories[i];
                        }
                    }
                }
            } else {
                b = categories;
            }
            return this.getCopyOfAnObject(b);
        }

        /* get only enable store item list*/
        public static getEnableStoreItemList() {
            let storeItems: dasa.StoreItem[] = dasa.Utility.storeItems;
            let disabledItemIds = dasa.Utility.storeSettings?.disabledItemIds;
            let b: any = [];
            if (disabledItemIds != undefined && disabledItemIds.length > 0) {
                if (storeItems != undefined && storeItems.length > 0) {
                    for (let i: any = 0; i < storeItems.length; i++) {
                        let isDisabled: boolean = false;
                        for (let k: any = 0; k < disabledItemIds.length; k++) {
                            if (disabledItemIds[k] == storeItems[i].id) {
                                isDisabled = true;
                                break;
                            }
                        }
                        if (isDisabled == false && this.isDisabledBrand(storeItems[i].brandId) == false
                            && this.isDisabledCategory(storeItems[i].categoryId) == false) {
                            b[b.length] = storeItems[i];
                        }
                    }
                }
            } else {
                b = storeItems;
            }
            return this.getCopyOfAnObject(b);
        }

        public static isDisabledCategory(categoryId: number): boolean {
            let isDisabled: boolean = false;
            let categories: any = dasa.Utility.categories;
            let disabledCategoryIds = dasa.Utility.storeSettings.disabledCategoryIds;
            if (disabledCategoryIds != undefined && disabledCategoryIds.length > 0) {
                for (let i = 0; i < disabledCategoryIds.length; i++) {
                    if (disabledCategoryIds[i] * 1 == categoryId * 1) {
                        isDisabled = true;
                        break;
                    }
                }
            }
            return isDisabled;
        }

        public static isDisabledBrand(brandId: number): boolean {
            let isDisabled: boolean = false;
            let brands: any = dasa.Utility.brands;
            let disabledBrandIds = dasa.Utility.storeSettings.disabledBrandIds;
            if (disabledBrandIds != undefined && disabledBrandIds.length > 0) {
                for (let i = 0; i < disabledBrandIds.length; i++) {
                    if (disabledBrandIds[i] * 1 == brandId * 1) {
                        isDisabled = true;
                        break;
                    }
                }
            }
            return isDisabled;
        }

        public static getStatusArray(): any {
            let status_data = [
                {
                    id: -1,
                    status: 'Select Status'
                },
                {
                    id: dasa.Constant.STATUS_ENABLED,
                    status: 'ENABLED'
                }, {
                    id: dasa.Constant.STATUS_DISABLED,
                    status: 'DISABLED'
                }
            ];
            return status_data;
        }

        /*
        Get user type buisness or buisness cum store admin
        */
        public static getUserTypeArray(): any {
            let usertype_data = [
                {
                    id: -1,
                    name: 'Select User Type'
                },
                {
                    id: dasa.Constant.USER_TYPE_BUSINESS_ADMIN,
                    name: 'Business Admin'
                }, {
                    id: dasa.Constant.USER_TYPE_BUSINESS_ADMIN_cum_STORE_ADMIN,
                    name: 'Business Cum Super Admin'
                }
            ];
            return usertype_data;
        }

        /*
        Get timingjson data
        */
        public static getTimingJson(): any {
            let timing_data = [
                { id: 1, day: "Monday", openingTime: "09:00", closingTime: "20:00" },
                { id: 2, day: "Tuesday", openingTime: "09:00", closingTime: "20:00" },
                { id: 3, day: "Wednesday", openingTime: "09:00", closingTime: "20:00" },
                { id: 4, day: "Thursday", openingTime: "09:00", closingTime: "20:00" },
                { id: 5, day: "Friday", openingTime: "09:00", closingTime: "20:00" },
                { id: 6, day: "Saturday", openingTime: "09:00", closingTime: "20:00" },
                { id: 7, day: "Sunday", openingTime: "09:00", closingTime: "20:00" }
            ];
            return timing_data;
        }
        public static getTimeObjectReservationDay(storeTime: any[],dateString:string): any {
            let day_value = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
            let curr_date = new Date(dateString);
            let get_data = storeTime.filter(itm => itm.day === day_value[curr_date.getUTCDay()]);
            return get_data;
        }

        public static getTimeObjectCurrentDay(storeTime: any[]): any {
            let day_value = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
            let curr_date = new Date();
            let get_data = storeTime.filter(itm => itm.day === day_value[curr_date.getUTCDay()]);
            return get_data;
        }
        public static getClosedTimeObjectCurrentDay(storeTime: any[]): any {
            let day_value = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
            let curr_date = new Date();
            let get_data = storeTime.filter(itm => itm.day === day_value[curr_date.getUTCDay()]);
            return get_data;
        }

        // public static getdRangeJson():any{
        //     let dRangeJson_data =[
        //         {startRange: "1",  endRange: "2", deliveryTime: "3"}
        //     ];
        //     return dRangeJson_data;
        // }

        /*
       Get dRangeJson data
       */
        public static getdRangeJson(): any {
            let timing_data = [
                { startRange: "", endRange: "", deliveryTime: "" }
            ];
            return timing_data;
        }

        /* Method to get is featured data */
        public static getIsFeaturedArray(): any {
            let isFeatured_data = [
                {
                    id: -1,
                    status: 'Select Featured Store'
                },
                {
                    id: dasa.Constant.FEATURED_STORE_YES,
                    status: 'Yes'
                },
                {
                    id: dasa.Constant.FEATURED_STORE_NO,
                    status: 'No'
                }
            ];
            return isFeatured_data;
        }

        /** 
         * Method to get delivery type
        **/
        public static getDeliveryTypeArray(): any {
            let deliverytype_data = [
                {
                    id: -1,
                    status: 'Select Delivery Type'
                },
                {
                    id: dasa.Constant.DELIVERY_TYPE_PICK_UP_ONLY,
                    status: 'Pick Up Only'
                },
                {
                    id: dasa.Constant.DELIVERY_TYPE_DELIVERY_ONLY,
                    status: 'Delivery Only'
                },
                {
                    id: dasa.Constant.DELIVERY_TYPE_PICKUP_DELIVERY_BOTH,
                    status: 'Pickup Delivery Both'
                }
            ];
            return deliverytype_data;
        }

        /* Method to get is featured data */
        public static getDiscountTypeArray(): any {
            let discountType_data = [
                {
                    id: -1,
                    name: 'Select Discount Type'
                },
                {
                    id: dasa.Constant.DISCOUNT_TYPE_FLAT,
                    name: 'Flat'
                },
                {
                    id: dasa.Constant.DISCOUNT_TYPE_PERCENT,
                    name: 'Percent'
                }
            ];
            return discountType_data;
        }

        /* Get Discount type flat & percent */
        public static getDiscountType(id: number) {
            let value;
            if (id === dasa.Constant.DISCOUNT_TYPE_PERCENT) {
                value = "Percent";
            }
            else if (id === dasa.Constant.DISCOUNT_TYPE_FLAT) {
                value = "Flat";
            }
            return value;
        }

        /* Get CMS type  and passed type in function*/
        public static getCMSType(id: number) {
            let value;
            if (id === 1) {
                value = "Driver";
            }
            else if (id === 2) {
                value = "Store";
            }
            else if (id === 3) {
                value = "Customer";
            }
            return value;
        }

        /*get unit type*/
        public static getUnitArray(): any {
            let unit_data = [
                {
                    id: -1,
                    name: 'Select Unit'
                },
                {
                    id: 1,
                    name: 'Kg'
                },
                {
                    id: 2,
                    name: 'Gm'
                },
                {
                    id: 3,
                    name: 'Ltrs'
                }
            ];
            return unit_data;
        }

        /**
         * Method used to get user by user id
         * @param userId 
         * @returns 
         */
        public static getUnitNameByID(unitId: number): string {
            let arry = this.getUnitArray();
            let unitName = "";
            for (let i: any = 0; i < arry.length; i++) {
                if (unitId == arry[i].id) {
                    unitName = arry[i].name;
                    break;
                }
            }
            return unitName;
        }

        /**
         * Method used to store latest utility object in session
        */
        public static putInSession(): void {
            if (window && window.sessionStorage) {
                let sObject: SessionObject = new SessionObject();
                sObject.business = dasa.Utility.business;
                sObject.selectedStoreDetails = dasa.Utility.selectedStoreDetails;
                sObject.stores = dasa.Utility.stores;
                sObject.storeSettings = dasa.Utility.storeSettings;
                sObject.storeConfigurations = dasa.Utility.storeConfigurations;
                sObject.brands = dasa.Utility.brands;
                sObject.categories = dasa.Utility.categories;
                sObject.storeItems = dasa.Utility.storeItems;
                sObject.country = dasa.Utility.country;
                sObject.selectedBusinessId = dasa.Utility.selectedBusinessId;
                sObject.selectedStoreId = dasa.Utility.selectedStoreId;
                sObject.loggedInUser = dasa.Utility.loggedInUser;
                sObject.carts = dasa.Utility.carts;
                sObject.complains = dasa.Utility.complains;
                sObject.uiSettings = dasa.Utility.uiSettings;
                sObject.temporaryOrderRequest = dasa.Utility.temporaryOrderRequest;
                sObject.promoCodeDetail = dasa.Utility.promoCodeDetail;
                sObject.addPoints = dasa.Utility.addPoints;
                sObject.cartType = dasa.Utility.cartType;
                // QR Table Ordering (Flow B) context — set as ad-hoc props on
                // dasa.Utility by table-landing.continueToMenu. Persist them so
                // a refresh on the menu/checkout keeps the table binding and
                // guest identity instead of collapsing into the generic flow.
                sObject.orderMode = (dasa.Utility as any).orderMode;
                sObject.tableId = (dasa.Utility as any).tableId;
                sObject.tableName = (dasa.Utility as any).tableName;
                sObject.tableGuestName = (dasa.Utility as any).tableGuestName;
                sObject.tableGuestPhone = (dasa.Utility as any).tableGuestPhone;
                sObject.tableGuestEmail = (dasa.Utility as any).tableGuestEmail;
                sObject.qrTableOrdering = (dasa.Utility as any).qrTableOrdering;
                sObject.serviceChargesForTableOrders = (dasa.Utility as any).serviceChargesForTableOrders;
                this.persistSession(sObject);
            }
        }

        /**
         * Write the session blob, shrinking it rather than failing.
         *
         * `storeItems` is an entire store catalogue and is unbounded, while
         * sessionStorage caps at roughly 5MB. On a large menu the write threw
         * QuotaExceededError straight out of putInSession, which killed the
         * caller's subscribe chain in whitelevel.getStoreSettingByStore and
         * left the app spinning on "LOADING" forever — the whole store became
         * unreachable because of a cache write.
         *
         * Session persistence only exists so a refresh keeps context; the live
         * data is already in dasa.Utility, and every screen that needs the
         * catalogue re-fetches it. So drop the heavy collections progressively
         * and, in the worst case, skip persistence entirely. Never throw.
         */
        private static persistSession(sObject: SessionObject): void {
            const shapes: SessionObject[] = [
                sObject,
                { ...sObject, storeItems: [] } as SessionObject,
                { ...sObject, storeItems: [], categories: [], brands: [],
                  stores: [], selectedStoreDetails: [] } as SessionObject,
            ];

            for (const shape of shapes) {
                try {
                    sessionStorage.setItem("dasaPosDb", JSON.stringify(shape));
                    return;
                } catch (e) {
                    // Quota (or a serialisation failure) — try the next, smaller shape.
                }
            }

            console.warn('[DasaAppUtil] session persistence skipped: payload exceeds sessionStorage quota');
        }
        
        /**
         * Methos used to load from session
         */
        /**
         * Re-publish the store's theme as CSS custom properties from whatever
         * uiSettings the session already holds.
         *
         * WhitelevelComponent is the only thing that publishes these, and it
         * only runs on the bootstrap route. A full page load straight to a deep
         * link — a bookmarked catering menu, a shared invoice URL, or any hard
         * refresh — never runs it, so the store data restores from session but
         * the tokens fall back to the :root defaults in styles.scss and the
         * page renders in the stock green instead of the store's colours.
         *
         * Whitelevel still owns fetching and persisting; this only replays what
         * was already stored, so the two cannot disagree.
         */
        public static applyThemeFromSession(): void {
            const ui: any = (dasa.Utility as any).uiSettings;
            if (!ui) { return; }
            const root = document.documentElement;
            const set = (name: string, value: any, fallback: string) => {
                const v = (value == undefined || value === '') ? fallback : value;
                root.style.setProperty(name, v);
            };
            set('--header-color',           ui.header,               '#034131');
            set('--ok-button-color',        ui.buttonSaveOk,         '#00046B');
            set('--cat-button-color',       ui.categoryButton,       '#deb887');
            set('--cancel-button-color',    ui.cancelButton,         '#dc3545');
            set('--menu-button-color',      ui.checkOutColor,        '#25F6AF');
            set('--left-menu-color',        ui.leftNavigationButton, '#ffffff');
            set('--footer-color',           ui.footer,               '#241C15');
            set('--body-bg-color',          ui.primaryColor,         '#d8eacc');
            set('--secondary-bg-color',     ui.secondaryColor,       '#ffffff');
            set('--body--text-color-color', ui.bodyText,             '#101010');
            set('--border-color',           ui.borderColor,          '#e5e7eb');
            set('--muted-text-color',       ui.mutedTextColor,       '#374151');
            set('--page-banner-color',      ui.pageBannerColor,      ui.secondaryColor || '#ffffff');
            set('--menu-bar-color',         ui.menuBarColor,         ui.header || '#034131');
            const perRow = Number(ui.cateringItemsPerRow);
            root.style.setProperty('--catering-items-per-row',
                String(perRow >= 1 && perRow <= 6 ? perRow : 3));
        }

        public static loadFromSession(): void {
            if (window && window.sessionStorage) {
                let k: any = sessionStorage.getItem("dasaPosDb");
                if (k != null && k != undefined) {
                    let obj = JSON.parse(k);
                    let sObject = obj as SessionObject;
                    dasa.Utility.business = sObject.business;
                    dasa.Utility.selectedStoreDetails = sObject.selectedStoreDetails;
                    dasa.Utility.stores = sObject.stores;
                    dasa.Utility.storeSettings = sObject.storeSettings;
                    dasa.Utility.storeConfigurations = sObject.storeConfigurations;
                    dasa.Utility.brands = sObject.brands;
                    dasa.Utility.categories = sObject.categories;
                    dasa.Utility.storeItems = sObject.storeItems;
                    dasa.Utility.country = sObject.country;
                    dasa.Utility.selectedBusinessId = sObject.selectedBusinessId;
                    dasa.Utility.selectedStoreId = sObject.selectedStoreId;
                    dasa.Utility.loggedInUser = sObject.loggedInUser;
                    // An EMPTY stored basket must never overwrite a live one.
                    //
                    // Every screen calls loadFromSession on init, and this line
                    // used to assign unconditionally. So any moment the stored
                    // copy was empty or stale while the in-memory basket had
                    // items — a persist that silently skipped, a write that
                    // landed after the read, a tab that never persisted — the
                    // next navigation wiped the basket with no warning and no
                    // dialog. Restoring is only meant to RECOVER a basket after
                    // a refresh, never to destroy one that already exists.
                    //
                    // Deliberately one-directional: a non-empty stored basket
                    // still wins, so a genuine refresh restores as before, and
                    // an explicit clear (removeFromSession, order placed, mode
                    // switch) sets the in-memory list itself rather than
                    // relying on this read.
                    const storedCarts = sObject.carts ?? [];
                    const liveCarts = dasa.Utility.carts ?? [];
                    if (storedCarts.length > 0 || liveCarts.length === 0) {
                        dasa.Utility.carts = sObject.carts;
                    }
                    dasa.Utility.complains = sObject.complains;
                    dasa.Utility.uiSettings = sObject.uiSettings;
                    dasa.Utility.temporaryOrderRequest = sObject.temporaryOrderRequest;
                    dasa.Utility.promoCodeDetail = sObject.promoCodeDetail;
                    dasa.Utility.addPoints = sObject.addPoints;
                    dasa.Utility.cartType = sObject.cartType;
                    // QR Table Ordering (Flow B) context — restore so a refresh
                    // keeps the customer in the real QR flow (table + guest)
                    // rather than falling back to the generic checkout.
                    (dasa.Utility as any).orderMode = sObject.orderMode;
                    (dasa.Utility as any).tableId = sObject.tableId;
                    (dasa.Utility as any).tableName = sObject.tableName;
                    (dasa.Utility as any).tableGuestName = sObject.tableGuestName;
                    (dasa.Utility as any).tableGuestPhone = sObject.tableGuestPhone;
                    (dasa.Utility as any).tableGuestEmail = sObject.tableGuestEmail;
                    (dasa.Utility as any).qrTableOrdering = sObject.qrTableOrdering;
                    (dasa.Utility as any).serviceChargesForTableOrders = sObject.serviceChargesForTableOrders;
                }
            }
        }

        /**
         * Method used to remove data from session
         * call this method at the time of logout
         */
        public static removeFromSession(): void {
            if (window && window.sessionStorage) {
                dasa.Utility.loggedInUser = new dasa.Customer();
                dasa.Utility.complains = [] as dasa.FeedbackComplain[];
                dasa.Utility.carts = [] as dasa.Cart[];
            }
        }
    }

    /**
    * All fields present in this class will be stored in session
    */
    export class SessionObject {
        public business: dasa.Business = {} as dasa.Business;
        public selectedStoreDetails: dasa.selectedStoreDetails[] = [] as dasa.selectedStoreDetails[];
        public stores: dasa.Store[] = [] as dasa.Store[];
        public storeSettings: dasa.StoreSettings = {} as dasa.StoreSettings;
        public storeConfigurations: dasa.StoreConfiguration = {} as dasa.StoreConfiguration;
        public brands: dasa.Brand[] = [] as dasa.Brand[];
        public categories: dasa.Category[] = [] as dasa.Category[];
        public storeItems: dasa.StoreItem[] = [] as dasa.StoreItem[];
        public country: dasa.Country = {} as dasa.Country;
        public selectedStoreId: number = -1;
        public selectedBusinessId: number = -1;
        public loggedInUser: dasa.Customer = {} as dasa.Customer;
        public carts: dasa.Cart[] = [] as dasa.Cart[];
        public complains: dasa.FeedbackComplain[] = [] as dasa.FeedbackComplain[];
        public uiSettings: dasa.UISettings = {} as dasa.UISettings;
        public temporaryOrderRequest: dasa.RequestObject = {} as dasa.RequestObject;
        public promoCodeDetail: dasa.RequestObject = {} as dasa.RequestObject;
        public addPoints: dasa.AddPoints = {} as dasa.AddPoints;
        public cartType: number = 1;
        // QR Table Ordering (Flow B) session context. Held on dasa.Utility as
        // ad-hoc props by table-landing.continueToMenu; persisted here so a
        // refresh keeps the table binding + guest identity.
        public orderMode: string | undefined = undefined;
        public tableId: any = undefined;
        public tableName: any = undefined;
        public tableGuestName: string | undefined = undefined;
        public tableGuestPhone: string | undefined = undefined;
        public tableGuestEmail: string | undefined = undefined;
        public qrTableOrdering: number | undefined = undefined;
        public serviceChargesForTableOrders: any = undefined;
    }
}